import { Button, LoadingButton } from 'components/lib';
import noop from 'lodash/noop';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslator } from '@bluebeam/react-i18n';

interface Props {
  id?: string;
  confirmText?: string;
  onConfirmClick?: (
    evt: React.MouseEvent<HTMLButtonElement>
  ) => void | Promise<void>;
  onConfirmClickSync?: (evt: React.MouseEvent<HTMLButtonElement>) => void;
  confirmDisabled?: boolean;
  hideConfirm?: boolean;
  cancelText?: string;
  onCancelClick?: (evt: React.MouseEvent<HTMLButtonElement>) => void;
  hideCancel?: boolean;
  secondaryText?: string;
  secondaryDisabled?: boolean;
  onSecondaryClick?: () => void | Promise<void>;
  hideSecondary?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const OverlayFooter: React.FC<Props> = (props: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    id,
    confirmText,
    cancelText,
    secondaryText,
    onCancelClick,
    onSecondaryClick,
    confirmDisabled,
    secondaryDisabled,
    hideCancel,
    hideConfirm,
    hideSecondary,
    children,
    onConfirmClickSync,
    onConfirmClick,
    isLoading,
  } = props;

  const { translate } = useTranslator(['buttons']);

  const handleOnConfirmClick = async (
    evt: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      if (onConfirmClickSync) onConfirmClickSync(evt);
      if (onConfirmClick) await onConfirmClick(evt);
    } catch {
      setIsSubmitting(false);
    }

    setIsSubmitting(false);
  };

  return (
    <div
      id={id}
      className="overlay-footer hrem-4p5 row px4 items-center justify-between bgd-white shrink-0 bt-solid bt-light-grey-1 bt-width-1"
    >
      <div>{children}</div>
      <div className="row items-center">
        {!hideSecondary ? (
          <Button
            id={`${id}-secondary`}
            size="small"
            variant="secondary"
            className="overlay-footer-secondary-btn mr3"
            onClick={onSecondaryClick}
            disabled={secondaryDisabled || isSubmitting || isLoading}
          >
            {secondaryText || translate('buttons:save')}
          </Button>
        ) : null}
        {!hideCancel ? (
          <Button
            id={`${id}-cancel`}
            size="small"
            variant="tertiary"
            className="overlay-footer-cancel-btn mr3"
            onClick={onCancelClick}
            disabled={isSubmitting || isLoading}
          >
            {cancelText || translate('buttons:cancel')}
          </Button>
        ) : null}
        {!hideConfirm ? (
          <LoadingButton
            type="button"
            variant="primary"
            size="small"
            id={`${id}-confirm`}
            className="overlay-footer-confirm-btn"
            onClick={(evt: React.MouseEvent<HTMLButtonElement>) =>
              handleOnConfirmClick(evt)
            }
            disabled={confirmDisabled}
            isLoading={isSubmitting || (isLoading !== undefined && isLoading)}
          >
            {confirmText || translate('buttons:confirm')}
          </LoadingButton>
        ) : null}
      </div>
    </div>
  );
};

export default OverlayFooter;

OverlayFooter.defaultProps = {
  confirmDisabled: false,
  hideConfirm: false,
  hideCancel: false,
  onConfirmClick: noop,
  onConfirmClickSync: noop,
  onCancelClick: noop,
  secondaryDisabled: false,
  onSecondaryClick: noop,
  hideSecondary: true,
  isLoading: false,
  children: undefined,
};
